/**
 * Patient 360° — clinical patient workspace (staff lens).
 *
 * Route: /patient-360/:partyId  (guarded by page.patients)
 * One patient, every angle: header band, rule-generated clinical summary,
 * care journey stepper, and role-adaptive tabs (clinical / visits /
 * financial / audit). The patient sees the same journey through the simple
 * portal lens on PatientDashboard — two lenses, one patient.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  patientAPI, carePlanAPI, visitAPI, prescriptionAPI, billingAPI, requestAPI,
} from '../../services/api';
import { ssPartyAPI } from '../../services/ssApi';
import Skeleton from '../../components/common/Skeleton';
import { TabBar } from '../../components/ss/layout';
import {
  T, FONT, CARD, HERO_GRADIENT, I, asList, fmtDate, fmtDateTime, fmtMoney,
  FadeSection, ChartCard, EmptyState, StatusBadge, Avatar, ListRow,
  PlannedPill, PolarStyles,
} from '../dashboard/shared';
import { deriveCareJourney } from './careJourney';

const up = (v) => String(v || '').toUpperCase();
const unData = (r) => r?.data ?? r;

// ── Small chips ───────────────────────────────────────────────────────────────
function FrostChip({ children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 11px', borderRadius: 999,
      background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      fontSize: 11, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

function TagChip({ label, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 11, fontWeight: 700, color,
      background: `${color}14`, border: `1px solid ${color}30`,
      padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── Risk severity ordering ────────────────────────────────────────────────────
const SEVERITY_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
const SEVERITY_COLOR = { CRITICAL: T.danger, HIGH: T.danger, MEDIUM: T.warning, LOW: T.info };

// ── Horizontal journey stepper (clinical lens) ────────────────────────────────
function JourneyStepper({ steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 4 }}>
      {steps.map((s, i) => {
        const last = i === steps.length - 1;
        const isDone = s.state === 'done';
        const isCurrent = s.state === 'current';
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', flex: last ? '0 0 auto' : 1, minWidth: 120 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: 120, flexShrink: 0 }}>
              {isDone ? (
                <span style={{
                  width: 26, height: 26, borderRadius: '50%', background: T.teal,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <I.check size={14} color="#fff" />
                </span>
              ) : isCurrent ? (
                <span className="hhms-p360-pulse" style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#fff', border: `3px solid ${T.primary}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary }} />
                </span>
              ) : (
                <span style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#fff', border: `2px solid ${T.border}`,
                }} />
              )}
              <div style={{
                fontSize: 11.5, fontWeight: isCurrent ? 800 : 700, marginTop: 8, lineHeight: 1.3,
                color: isDone ? T.text : isCurrent ? T.primary : T.muted,
              }}>
                {s.label}
              </div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 3, lineHeight: 1.35 }}>{s.caption}</div>
            </div>
            {!last && (
              <span style={{
                flex: 1, height: 2, marginTop: 12, minWidth: 16,
                background: isDone ? T.teal : T.border,
              }} />
            )}
          </div>
        );
      })}
      <style>{`
        @keyframes hhms-p360-ring {
          0%, 100% { box-shadow: 0 0 0 0 ${T.primary}40; }
          50%      { box-shadow: 0 0 0 6px ${T.primary}1A; }
        }
        .hhms-p360-pulse { animation: hhms-p360-ring 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .hhms-p360-pulse { animation: none; } }
      `}</style>
    </div>
  );
}

// ── Rule-generated clinical summary (no fake AI — deterministic composition) ──
function buildSummary({ patient, profile, carePlans, visits }) {
  const sentences = [];
  const name = patient?.name || 'This patient';
  const bits = [];
  if (profile?.dob) {
    const age = Math.floor((Date.now() - new Date(profile.dob).getTime()) / (365.25 * 864e5));
    if (age >= 0 && age < 130) bits.push(`${age}-year-old`);
  }
  if (profile?.gender) bits.push(String(profile.gender).toLowerCase());
  sentences.push(bits.length ? `${name} is a ${bits.join(' ')} patient.` : `${name} is a registered patient.`);

  const conditions = profile?.chronic_conditions || [];
  if (conditions.length) {
    sentences.push(`Known chronic condition${conditions.length > 1 ? 's' : ''}: ${conditions.slice(0, 3).join(', ')}.`);
  }
  const activePlan = (carePlans || []).find(c => up(c.status) === 'ACTIVE');
  if (activePlan) {
    sentences.push(`Currently on an active care plan${activePlan.diagnosis ? ` for ${activePlan.diagnosis}` : ''}${activePlan.review_date ? `, review due ${fmtDate(activePlan.review_date)}` : ''}.`);
  }
  const monthAgo = new Date(Date.now() - 30 * 864e5);
  const recent = (visits || []).filter(v => v.scheduled_start && new Date(v.scheduled_start) >= monthAgo).length;
  sentences.push(recent
    ? `${recent} visit${recent > 1 ? 's' : ''} in the last 30 days.`
    : 'No visits recorded in the last 30 days.');

  const allergies = profile?.allergies || [];
  if (allergies.length) {
    sentences.push(`Caution — documented allergies: ${allergies.join(', ')}.`);
  }
  return sentences.join(' ');
}

// ── Totals strip for financial tab ────────────────────────────────────────────
function TotalsStrip({ invoices }) {
  const billed = invoices.reduce((s, i) => s + Number(i.total_amount ?? i.amount ?? 0), 0);
  const paid = invoices
    .filter(i => up(i.payment_status || i.status) === 'PAID')
    .reduce((s, i) => s + Number(i.total_amount ?? i.amount ?? 0), 0);
  const cells = [
    { label: 'Total billed', value: fmtMoney(billed), color: T.primary },
    { label: 'Paid', value: fmtMoney(paid), color: T.green },
    { label: 'Outstanding', value: fmtMoney(billed - paid), color: T.warning },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
      {cells.map(c => (
        <div key={c.label} style={{ ...CARD, padding: '14px 16px' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.color }}>{c.value}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 3 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function PatientWorkspace() {
  const { partyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const roles = user?.roles || [];

  const isAdmin = roles.includes('ADMIN');
  const isClinician = isAdmin || roles.includes('DOCTOR') || roles.includes('NURSE');
  const isBilling = roles.includes('BILLING');
  const isQA = roles.includes('QA');

  // IDOR guard: a PATIENT may only view their own 360° workspace.
  // Staff roles (ADMIN, DOCTOR, NURSE, BILLING, QA) may view any patient.
  const isPatientRole = roles.includes('PATIENT') && !isClinician && !isAdmin && !isBilling && !isQA;
  if (isPatientRole && user?.party_id && String(partyId) !== String(user.party_id)) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#7b1e3c' }}>
        <h2 style={{ fontSize: 20, marginBottom: 8 }}>Access Denied</h2>
        <p style={{ fontSize: 14, color: '#555' }}>You are not authorised to view this patient record.</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: 16, padding: '8px 20px', background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [flags, setFlags] = useState([]);
  const [consents, setConsents] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [visits, setVisits] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);

  const load = useCallback(() => {
    if (!partyId) return;
    setLoading(true);
    Promise.allSettled([
      patientAPI.get(partyId),
      patientAPI.getFlags(partyId).catch(() => null),
      patientAPI.getConsents(partyId).catch(() => null),
      carePlanAPI.list({ patient_id: partyId, limit: 100 }).catch(() => null),
      visitAPI.list({ patient_id: partyId, limit: 200 }).catch(() => null),
      prescriptionAPI.list({ patient_id: partyId, limit: 100 }).catch(() => null),
      billingAPI.list({ patient_id: partyId, limit: 100 }).catch(() => null),
      requestAPI.list({ patient_id: partyId, limit: 100 }).catch(() => null),
      isAdmin ? ssPartyAPI.auditHistory(partyId, { limit: 25 }).catch(() => null) : Promise.resolve(null),
    ]).then(([p, fl, co, cp, vi, rx, bi, rq, au]) => {
      if (p.status === 'fulfilled') setPatient(unData(p.value));
      if (fl.status === 'fulfilled') setFlags(asList(fl.value));
      if (co.status === 'fulfilled') setConsents(asList(co.value));
      if (cp.status === 'fulfilled') setCarePlans(asList(cp.value).filter(c => !c.patient_id || String(c.patient_id) === String(partyId)));
      if (vi.status === 'fulfilled') setVisits(asList(vi.value).filter(v => !v.patient_id || String(v.patient_id) === String(partyId)));
      if (rx.status === 'fulfilled') setPrescriptions(asList(rx.value).filter(r => !r.patient_id || String(r.patient_id) === String(partyId)));
      if (bi.status === 'fulfilled') setInvoices(asList(bi.value).filter(i => !i.patient_id || String(i.patient_id) === String(partyId)));
      if (rq.status === 'fulfilled') setRequests(asList(rq.value).filter(r => !r.patient_id || String(r.patient_id) === String(partyId)));
      if (au.status === 'fulfilled') setAuditEvents(asList(au.value));
      setLoading(false);
    });
  }, [partyId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  // ── Role-adaptive tab set ───────────────────────────────────────────────
  const tabs = useMemo(() => {
    const t = [];
    if (isClinician) t.push({ id: 'clinical', label: 'Clinical record' });
    t.push({ id: 'visits', label: 'Visits' }); // all staff roles
    if (isBilling || isAdmin || isClinician) t.push({ id: 'financial', label: 'Financial' });
    if (isAdmin) t.push({ id: 'audit', label: 'Audit trail' });
    return t;
  }, [isClinician, isBilling, isAdmin]);
  const [tab, setTab] = useState(null);
  const activeTab = tab && tabs.some(x => x.id === tab) ? tab : (tabs[0]?.id || 'visits');

  const profile = patient?.profile || {};
  const journey = useMemo(
    () => deriveCareJourney({ requests, carePlans, visits }),
    [requests, carePlans, visits]
  );
  // QA lens: overview + journey + visits only (no clinical/financial detail).
  const showJourney = isClinician || isQA || isAdmin;
  const showSummary = true; // overview is shared across staff lenses

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={140} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.Card rows={2} />
      <div style={{ marginTop: 16 }}><Skeleton.Card rows={4} /></div>
    </div>
  );

  if (!patient) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <div style={{ ...CARD, padding: 40 }}>
        <EmptyState icon={I.alert} color={T.warning} title="Patient not found"
          caption="This record may not exist, or you may not have access to it." />
      </div>
    </div>
  );

  // Header derivations
  const age = profile.dob
    ? Math.floor((Date.now() - new Date(profile.dob).getTime()) / (365.25 * 864e5))
    : null;
  const activeFlags = flags.filter(f => f.is_active !== false && !f.resolved_at);
  const topFlag = [...activeFlags].sort((a, b) =>
    (SEVERITY_RANK[up(b.severity)] || 0) - (SEVERITY_RANK[up(a.severity)] || 0))[0];
  const careTeam = [...new Map(
    visits.filter(v => v.nurse_name).map(v => [v.nurse_name, v])
  ).keys()].slice(0, 4);

  const summaryText = buildSummary({ patient, profile, carePlans, visits });

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      {/* ── 1 · Patient header band ─────────────────────────────────────── */}
      <FadeSection index={0}>
        <div style={{
          position: 'relative', overflow: 'hidden', marginBottom: 18,
          padding: '24px 28px', background: HERO_GRADIENT,
          borderRadius: 20, color: '#fff',
          boxShadow: '0 14px 36px -14px rgba(13,27,46,.5)',
        }}>
          <div aria-hidden style={{
            position: 'absolute', top: -120, right: -90, width: 320, height: 320,
            borderRadius: '50%', background: `radial-gradient(circle, ${T.sky}33 0%, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 18, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
              <Avatar name={patient.name} size={56} />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-.4px', color: '#fff' }}>
                    {patient.name || 'Unnamed patient'}
                  </h1>
                  <StatusBadge value={patient.status || 'ACTIVE'} />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 4, fontFamily: 'monospace' }}>
                  {partyId}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {age !== null && <FrostChip>{age} yrs</FrostChip>}
                  {profile.gender && <FrostChip>{String(profile.gender)}</FrostChip>}
                  {profile.blood_group && <FrostChip><I.heart size={11} color="#FCA5A5" /> {profile.blood_group}</FrostChip>}
                  {topFlag && (
                    <FrostChip>
                      <I.alert size={11} color="#FCD34D" />
                      {up(topFlag.severity)} risk · {String(topFlag.flag_type || '').replace(/_/g, ' ')}
                    </FrostChip>
                  )}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.6)', textTransform: 'uppercase', letterSpacing: '.7px', marginBottom: 8 }}>
                Assigned team
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {careTeam.length
                  ? careTeam.map(n => <FrostChip key={n}><I.nurse size={11} color="#BEE3FF" /> {n}</FrostChip>)
                  : <FrostChip>No nurses assigned yet</FrostChip>}
              </div>
            </div>
          </div>
        </div>
      </FadeSection>

      {/* ── 2 · Clinical summary (rule-generated) ──────────────────────────── */}
      {showSummary && (
        <FadeSection index={1}>
          <div style={{ ...CARD, padding: '18px 22px', marginBottom: 18, borderLeft: `4px solid ${T.info}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <I.clipboard size={14} color={T.info} />
              <span style={{ fontSize: 13, fontWeight: 700 }}>Clinical summary</span>
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: T.info, background: `${T.info}12`,
                border: `1px solid ${T.info}30`, padding: '2px 8px', borderRadius: 999,
                letterSpacing: '.3px',
              }}>
                Auto-generated summary — verify against the record
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: T.text }}>{summaryText}</p>
          </div>
        </FadeSection>
      )}

      {/* ── 3 · Care journey (shared derivation, horizontal stepper) ───────── */}
      {showJourney && (
        <FadeSection index={2}>
          <ChartCard title="Care journey" subtitle="Derived from requests, care plans and visits — same logic the patient sees in their portal">
            {journey.hasAny ? (
              <JourneyStepper steps={journey.steps} />
            ) : (
              <EmptyState icon={I.heart} color={T.teal} title="No journey data yet"
                caption="No care requests, plans or visits are recorded for this patient." />
            )}
          </ChartCard>
        </FadeSection>
      )}

      {/* ── 4 · Tabbed record ──────────────────────────────────────────────── */}
      <FadeSection index={3} style={{ marginTop: 18 }}>
        <div style={{ ...CARD, padding: '8px 22px 22px' }}>
          <TabBar tabs={tabs} active={activeTab} onChange={setTab} style={{ marginBottom: 18 }} />

          {/* ── Clinical record ── */}
          {activeTab === 'clinical' && isClinician && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {activeFlags.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  borderRadius: 12, background: `${T.danger}0F`, border: `1px solid ${T.danger}26`,
                  borderLeft: `4px solid ${T.danger}`,
                }}>
                  <I.alert size={16} color={T.danger} />
                  <div style={{ fontSize: 12.5, fontWeight: 600, flex: 1 }}>
                    {activeFlags.length} active clinical flag{activeFlags.length > 1 ? 's' : ''}:&nbsp;
                    {activeFlags.slice(0, 4).map(f =>
                      `${String(f.flag_type || '').replace(/_/g, ' ')} (${up(f.severity)})`).join(' · ')}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                <ChartCard title="Medical profile" subtitle="Allergies, conditions & notes">
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Allergies</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {(profile.allergies || []).length
                      ? profile.allergies.map(a => <TagChip key={a} label={a} color={T.danger} />)
                      : <span style={{ fontSize: 12, color: T.muted }}>None documented</span>}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Chronic conditions</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {(profile.chronic_conditions || []).length
                      ? profile.chronic_conditions.map(c => <TagChip key={c} label={c} color={T.warning} />)
                      : <span style={{ fontSize: 12, color: T.muted }}>None documented</span>}
                  </div>
                  {profile.medical_notes && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Notes</div>
                      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55 }}>{profile.medical_notes}</p>
                    </>
                  )}
                </ChartCard>

                <ChartCard title="Care plans" subtitle={`${carePlans.length} on record`}>
                  {carePlans.length ? carePlans.slice(0, 8).map((c, i) => (
                    <ListRow
                      key={c.care_plan_id || i}
                      left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.teal}12` }}><I.clipboard size={16} color={T.teal} /></span>}
                      title={c.diagnosis || c.title || c.plan_name || `Care plan #${i + 1}`}
                      sub={[
                        c.start_date ? `Start ${fmtDate(c.start_date)}` : null,
                        c.review_date ? `Review ${fmtDate(c.review_date)}` : null,
                        c.end_date ? `End ${fmtDate(c.end_date)}` : null,
                        c.goals ? `Goals: ${Array.isArray(c.goals) ? c.goals.join('; ') : c.goals}` : null,
                      ].filter(Boolean).join(' · ') || null}
                      right={<StatusBadge value={c.status} />}
                    />
                  )) : (
                    <EmptyState icon={I.clipboard} color={T.teal} title="No care plans" caption="No care plans recorded for this patient yet." />
                  )}
                </ChartCard>

                <ChartCard title="Prescriptions" subtitle={`${prescriptions.length} on record`}>
                  {prescriptions.length ? prescriptions.slice(0, 8).map((rx, i) => (
                    <ListRow
                      key={rx.prescription_id || i}
                      left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.purple}12` }}><I.pill size={16} color={T.purple} /></span>}
                      title={rx.medication_name || rx.drug_name || rx.name || `Prescription #${i + 1}`}
                      sub={[rx.dosage || rx.dose, rx.frequency].filter(Boolean).join(' · ') || null}
                      right={<StatusBadge value={rx.status} />}
                    />
                  )) : (
                    <EmptyState icon={I.pill} color={T.purple} title="No prescriptions" caption="No prescriptions recorded for this patient yet." />
                  )}
                </ChartCard>

                <ChartCard title="Consents" subtitle="Patient consent records">
                  {consents.length ? consents.slice(0, 8).map((co, i) => (
                    <ListRow
                      key={co.consent_id || i}
                      left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.info}12` }}><I.shield size={16} color={T.info} /></span>}
                      title={String(co.consent_type || co.type || 'Consent').replace(/_/g, ' ')}
                      sub={co.granted_at ? `Granted ${fmtDate(co.granted_at)}` : (co.created_at ? `Recorded ${fmtDate(co.created_at)}` : null)}
                      right={<StatusBadge value={co.status || (co.granted ? 'ACTIVE' : co.revoked_at ? 'REVOKED' : 'PENDING')} />}
                    />
                  )) : (
                    <EmptyState icon={I.shield} color={T.info} title="No consents on file" caption="Consent records for this patient will appear here." />
                  )}
                </ChartCard>
              </div>
            </div>
          )}

          {/* ── Visits ── */}
          {activeTab === 'visits' && (
            <ChartCard title="Visit history" subtitle={`${visits.length} visit${visits.length === 1 ? '' : 's'} for this patient`}>
              {visits.length ? [...visits]
                .sort((a, b) => new Date(b.scheduled_start || 0) - new Date(a.scheduled_start || 0))
                .slice(0, 25).map((v, i) => {
                  const hasReport = Boolean(v.report_id || v.has_report || v.clinical_report_id);
                  return (
                    <ListRow
                      key={v.visit_id || i}
                      left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.primary}12` }}><I.calendar size={16} color={T.primary} /></span>}
                      title={fmtDateTime(v.scheduled_start)}
                      sub={v.nurse_name ? `Nurse ${v.nurse_name}` : 'Nurse unassigned'}
                      right={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {hasReport && <TagChip label="Report filed" color={T.green} />}
                          <StatusBadge value={v.status} />
                        </span>
                      }
                    />
                  );
                }) : (
                <EmptyState icon={I.calendar} color={T.primary} title="No visits" caption="No visits have been scheduled for this patient yet." />
              )}
            </ChartCard>
          )}

          {/* ── Financial ── */}
          {activeTab === 'financial' && (isBilling || isAdmin || isClinician) && (
            <div>
              <TotalsStrip invoices={invoices} />
              <ChartCard title="Invoices" subtitle={`${invoices.length} invoice${invoices.length === 1 ? '' : 's'} for this patient`}>
                {invoices.length ? invoices.slice(0, 25).map((inv, i) => (
                  <ListRow
                    key={inv.invoice_id || i}
                    left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.warning}12` }}><I.coin size={16} color={T.warning} /></span>}
                    title={fmtMoney(inv.total_amount ?? inv.amount)}
                    sub={[
                      inv.invoice_number ? `Invoice ${inv.invoice_number}` : null,
                      inv.due_date ? `Due ${fmtDate(inv.due_date)}` : null,
                    ].filter(Boolean).join(' · ') || null}
                    right={<StatusBadge value={inv.payment_status || inv.status} />}
                  />
                )) : (
                  <EmptyState icon={I.coin} color={T.warning} title="No invoices" caption="No billing activity recorded for this patient yet." />
                )}
              </ChartCard>
            </div>
          )}

          {/* ── Audit trail (ADMIN only) ── */}
          {activeTab === 'audit' && isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                borderRadius: 12, background: `${T.purple}0D`, border: `1px solid ${T.purple}26`,
              }}>
                <I.clock size={15} color={T.purple} />
                <span style={{ fontSize: 12.5, flex: 1 }}>
                  Full per-patient audit timeline lands with M10 — below is the party-record audit history available today.
                </span>
                <PlannedPill label="M10" />
              </div>
              <ChartCard title="Party record changes" subtitle="Audit events on this patient's party record">
                {auditEvents.length ? auditEvents.slice(0, 25).map((e, i) => (
                  <ListRow
                    key={e.event_id || e.audit_id || i}
                    left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.purple}12` }}><I.shield size={16} color={T.purple} /></span>}
                    title={String(e.action || e.event_type || e.operation || 'Change').replace(/_/g, ' ')}
                    sub={[
                      e.actor_name || e.changed_by || e.user_name,
                      e.created_at || e.occurred_at || e.timestamp ? fmtDateTime(e.created_at || e.occurred_at || e.timestamp) : null,
                    ].filter(Boolean).join(' · ') || null}
                  />
                )) : (
                  <EmptyState icon={I.shield} color={T.purple} title="No audit events available"
                    caption="No party-record audit events were returned for this patient." />
                )}
              </ChartCard>
            </div>
          )}
        </div>
      </FadeSection>

      <div style={{ marginTop: 14 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 700, color: T.muted, fontFamily: FONT, padding: 0,
          }}
        >
          ← Back
        </button>
      </div>

      <PolarStyles />
    </div>
  );
}
